/**
 * Resonance Atlas catalog client (PSI-first onboarding).
 *
 * Fetched from ``GET /api/coaching/growth-palette`` — a categorized board
 * of capability cards the learner taps to express broad PSI, plus the
 * opening prompt and optional-anchor copy.  Memoized for the page
 * lifetime (small, static, consumed by the atlas screen).
 */

import { useEffect, useState } from "react";

export interface PaletteCard {
  cardId: string;
  title: string;
  subtitle: string;
  category: string;
  atlasRegions: string[];
  proofTypes: string[];
  textureHints: string[];
  difficultyCeiling: string;
}

export interface PaletteCategory {
  id: string;
  name: string;
}

export interface GrowthPaletteCatalog {
  schemaVersion: number;
  prompt: string;
  anchorPrompt: string;
  anchorPlaceholder: string;
  categories: PaletteCategory[];
  cards: PaletteCard[];
}

// ── In-memory cache ─────────────────────────────────────────────────

let _palettePromise: Promise<GrowthPaletteCatalog> | null = null;

export function fetchGrowthPalette(): Promise<GrowthPaletteCatalog> {
  if (_palettePromise == null) {
    _palettePromise = fetch("/api/coaching/growth-palette", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    }).then(async (res) => {
      if (!res.ok) {
        _palettePromise = null;
        throw new Error(
          `growth-palette fetch failed: ${res.status} ${res.statusText}`
        );
      }
      return (await res.json()) as GrowthPaletteCatalog;
    });
  }
  return _palettePromise;
}

/** Reset the cache.  Exported only for tests. */
export function _resetGrowthPaletteCache(): void {
  _palettePromise = null;
}

// ── React hook ──────────────────────────────────────────────────────

export interface UseGrowthPaletteResult {
  catalog: GrowthPaletteCatalog | null;
  cardsById: Record<string, PaletteCard>;
  loading: boolean;
  error: Error | null;
}

const EMPTY_CARDS: Record<string, PaletteCard> = {};

export function useGrowthPalette(): UseGrowthPaletteResult {
  const [catalog, setCatalog] = useState<GrowthPaletteCatalog | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchGrowthPalette().then(
      (c) => { if (!cancelled) setCatalog(c); },
      (e) => { if (!cancelled) setError(e); },
    );
    return () => { cancelled = true; };
  }, []);

  const cardsById = catalog == null
    ? EMPTY_CARDS
    : Object.fromEntries(catalog.cards.map((c) => [c.cardId, c]));

  return {
    catalog,
    cardsById,
    loading: catalog == null && error == null,
    error,
  };
}
